import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDateTime, fmtTime } from "@/lib/format";
import { CalendarClock, Plus, Sparkles, Trash2, Loader2, Users, Video } from "lucide-react";

const emptyForm = { title: "", agenda: "", datetime: "", end_time: "", meeting_link: "", participants: [] };

export default function Meetings() {
  const { user } = useAuth();
  const isMgr = user?.role === "manager" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setItems((await api.get("/meetings")).data);
    if (isMgr) setEmployees((await api.get("/employees")).data.filter((e) => e.status === "active"));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = (id) => setForm((f) => ({
    ...f, participants: f.participants.includes(id) ? f.participants.filter((p) => p !== id) : [...f.participants, id],
  }));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/meetings", {
        title: form.title, agenda: form.agenda, participants: form.participants,
        datetime: new Date(form.datetime).toISOString(),
        end_time: form.end_time ? new Date(form.end_time).toISOString() : undefined,
        meeting_link: form.meeting_link || undefined,
      });
      toast.success("Meeting scheduled.");
      setOpen(false); setForm(emptyForm);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const suggestAgenda = async () => {
    if (!form.title) { toast.warning("Add a title first."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/meetings/suggest-agenda", { title: form.title, context: form.agenda });
      setForm((f) => ({ ...f, agenda: data.agenda }));
      toast.success("Agenda drafted by AI.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const remove = async (m) => { await api.delete(`/meetings/${m.id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-emerald-400" />
          <h1 className="font-display text-2xl font-bold">Meetings</h1>
        </div>
        {isMgr && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="new-meeting-btn"><Plus className="w-4 h-4 mr-1" /> Plan meeting</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121816] border-emerald-500/20 max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Plan a meeting</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="meeting-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start</Label><Input type="datetime-local" value={form.datetime} onChange={(e) => setForm({ ...form, datetime: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="meeting-datetime-input" /></div>
                  <div><Label>End</Label><Input type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="meeting-end-input" /></div>
                </div>
                <div><Label>Meeting link (optional)</Label><Input type="url" placeholder="https://meet.google.com/..." value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="meeting-link-input" /></div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Agenda</Label>
                    <Button type="button" size="sm" variant="ghost" className="text-emerald-400 h-7" onClick={suggestAgenda} disabled={aiBusy} data-testid="meeting-ai-agenda-btn">
                      {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Suggest with AI</>}
                    </Button>
                  </div>
                  <Textarea rows={5} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="meeting-agenda-input" />
                </div>
                <div>
                  <Label>Participants</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {employees.map((e) => (
                      <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={form.participants.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`participant-${e.id}`} />
                        {e.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy || !form.title || !form.datetime} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="meeting-create-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="card-emerald p-12 text-center text-muted-foreground">No meetings planned.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((m) => (
            <div key={m.id} className="card-emerald p-5" data-testid="meeting-planner-card">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{m.title}</h3>
                  <p className="text-sm text-emerald-300 mt-0.5">
                    {fmtDateTime(m.datetime)}{m.end_time && <span className="text-muted-foreground"> – {fmtTime(m.end_time)}</span>}
                  </p>
                </div>
                {isMgr && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => remove(m)} data-testid="meeting-delete-btn"><Trash2 className="w-4 h-4" /></Button>}
              </div>
              {m.agenda && <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">{m.agenda}</p>}
              {m.meeting_link && (
                <a href={m.meeting_link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block" data-testid="meeting-join-link">
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold"><Video className="w-4 h-4 mr-1.5" /> Join Meeting</Button>
                </a>
              )}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
                <Users className="w-3.5 h-3.5" /> {m.participants?.length || 0} participants
                <Badge variant="outline" className="border-emerald-500/30 ml-auto">by {m.organizer}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
